import { useEffect, useRef, useState } from 'react';
import { useBlocker, useLocation, useNavigate } from 'react-router';

interface UseNavigationBlockerOptions {
    shouldBlock: boolean;
    onConfirm: () => void;
}

interface UseNavigationBlockerResult {
    openDialog: boolean;
    handleConfirm: () => void | Promise<void>;
    handleCancel: () => void;
    handleContinue: () => void;
}

export function useNavigationBlocker({
    shouldBlock,
    onConfirm,
}: UseNavigationBlockerOptions): UseNavigationBlockerResult {
    const location = useLocation();
    const navigate = useNavigate();
    const blocker = useBlocker(shouldBlock);
    const blockerRef = useRef(blocker);
    blockerRef.current = blocker;
    const pendingLocationRef = useRef<string | null>(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (blocker.state === 'blocked') {
            const destination = blocker.location.pathname;
            if (destination.includes(location.pathname)) {
                // If the destination is a deeper route of the current path, allow navigation without blocking
                blocker.proceed?.();
                return;
            }
            console.log('Navigation attempt to:', destination);
            pendingLocationRef.current = destination;
            setOpen(true);
        }
    }, [blocker, location]);

    return {
        openDialog: open,
        handleConfirm: async () => {
            const pendingLocation = pendingLocationRef.current;
            pendingLocationRef.current = null;
            await onConfirm();
            setOpen(false);
            // Never call proceed() after an async operation — the router's internal
            // state may have already transitioned. Navigate manually instead.
            if (pendingLocation) {
                navigate(pendingLocation);
            }
        },
        handleCancel: () => {
            blockerRef.current.reset?.();
            pendingLocationRef.current = null;
            setOpen(false);
        },
        handleContinue: () => {
            if (blockerRef.current.state === 'blocked') {
                blockerRef.current.proceed?.();
            } else if (pendingLocationRef.current) {
                navigate(pendingLocationRef.current);
            }
            pendingLocationRef.current = null;
            setOpen(false);
        },
    };
}
